import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { AiOutlineWarning } from 'react-icons/ai';
import { BiCalendarAlt } from 'react-icons/bi';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';

import ButtonGhost from '@/components/Button/ButtonGhost';
import ButtonSmall from '@/components/Button/ButtonSmall';
import inputStyles from '@/components/Inputs/inputs.module.scss';
import Toggle from '@/components/Inputs/Toggle';
import SegmentedControl from '@/components/SegmentedControl/SegmentedControl';
import { TimeSelect } from '@/components/TimeSelect';

import styles from './DateTimePicker.module.scss';
import { buildDayCells, isSameDay, startOfDay, WEEKDAYS } from './days';

export type DateRangeFieldProps = {
  label?: string;
  placeholder: string;
  /** The start, as ISO. */
  value?: string | null;
  /** The end, as ISO. Empty until the second tap, and may stay empty where the form allows it. */
  endValue?: string | null;
  /** Fires on every change — a day tapped, a wheel come to rest — with both ends as ISO. */
  onRangeChange(start: string | null, end: string | null): void;
  minDate?: Date;
  maxDate?: Date;
  mandatory?: boolean;
  /** Off for date-only forms and for an all-day event: no time steps, both ends at midnight. */
  showTimeInput?: boolean;
  /** With `onAllDayChange`, an All day toggle heads the card; on, it hides the times. */
  allDay?: boolean;
  onAllDayChange?(allDay: boolean): void;
  /** The folded field says "– pick end" while the end is missing. */
  endRequired?: boolean;
  /** What the two ends are called on the steps, e.g. Arrival / Departure on a berth. */
  startLabel?: string;
  endLabel?: string;
  error?: string;
  warning?: string;
  className?: string;
  /** Starts open and has no Done, for a step that exists only to pick these dates: every tap
      is already saved, so there is nothing to confirm. The field above still folds the card. */
  inline?: boolean;
};

type Step = 'dates' | 'start' | 'end';
type End = 'start' | 'end';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
/* ponytail: fixed pause before the card advances to the times; tune it in real use. */
const SETTLE_MS = 500;

/* `Nov 26, 2026`, the app's one date format (voice guide D7b). */
const dayText = (d: Date) =>
  d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const timeText = (d: Date) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const lengthText = (s: Date, e: Date) => {
  if (!isSameDay(s, e)) {
    const days = Math.round((startOfDay(e).getTime() - startOfDay(s).getTime()) / DAY) + 1;
    return `${days} days`;
  }
  const mins = Math.round((e.getTime() - s.getTime()) / MINUTE);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return [h && `${h} h`, (m || !h) && `${m} min`].filter(Boolean).join(' ');
};

/** The latest time a one-day range can end: the wheel's last row. */
const lastOn = (d: Date) => {
  const last = new Date(d);
  last.setHours(23, 55, 0, 0);
  return last;
};

/**
 * **A start and an end from one calendar.** The range mode of `DateTimePicker`,
 * laid out in place rather than in a popover: the field opens into a card, the
 * first tap sets the start and the second the end, then the card walks on to
 * the start and end times on a wheel and folds back into one line.
 *
 * Once a range exists an end can be dragged to another day, a tap moves
 * whichever end is nearer, and tapping an end picks it up so the next tap
 * moves it. An end before the start cannot be
 * chosen: those days are disabled, and on one day the end wheel snaps back.
 */
export const DateRangeField = ({
  label,
  placeholder,
  value,
  endValue,
  onRangeChange,
  minDate,
  maxDate,
  mandatory,
  showTimeInput = true,
  allDay = false,
  onAllDayChange,
  endRequired,
  startLabel = 'Starts',
  endLabel = 'Ends',
  error,
  warning,
  className,
  inline = false,
}: DateRangeFieldProps) => {
  const start = value ? new Date(value) : null;
  const end = endValue ? new Date(endValue) : null;
  const timed = showTimeInput && !allDay;

  const [open, setOpen] = useState(inline);
  const [step, setStep] = useState<Step>('dates');
  const [view, setView] = useState(() =>
    startOfDay(start ?? (minDate && minDate > new Date() ? minDate : new Date())),
  );
  const [grab, setGrab] = useState<End | null>(null);
  /* Until the times have been seen once, Next on the month goes on to them. */
  const [timesSet, setTimesSet] = useState(() => Boolean(value && endValue));
  const [clamped, setClamped] = useState(false);
  const timer = useRef(0);
  /* A drag that has left its day, so the click that ends it is not also a tap. */
  const drag = useRef<{ moved: boolean } | null>(null);

  useEffect(() => {
    const pending = timer;
    return () => clearTimeout(pending.current);
  }, []);

  const commit = (s: Date | null, e: Date | null) =>
    onRangeChange(s ? s.toISOString() : null, e ? e.toISOString() : null);

  /** The day, at the time carried by `time`, or at the next full hour. */
  const at = (day: Date, time: Date | null) => {
    const d = new Date(day);
    if (!timed) d.setHours(0, 0, 0, 0);
    else if (time) d.setHours(time.getHours(), time.getMinutes(), 0, 0);
    else d.setHours(Math.min(new Date().getHours() + 1, 23), 0, 0, 0);
    return d;
  };

  /** On one day the end never comes before the start: it moves to an hour after. */
  const tidy = (s: Date, e: Date) => {
    if (!timed || !isSameDay(s, e) || e >= s) return e;
    return new Date(Math.max(s.getTime(), Math.min(s.getTime() + HOUR, lastOn(s).getTime())));
  };

  const fold = () => {
    clearTimeout(timer.current);
    setOpen(false);
    setStep('dates');
    setGrab(null);
    setClamped(false);
    if (start && end) setTimesSet(true);
  };

  /* Picking the last day walks on to the times. Moving an end of a range that
     already exists leaves the card on the month; only Done folds it. */
  const settle = () => {
    clearTimeout(timer.current);
    if (!timed) return;
    timer.current = window.setTimeout(() => setStep('start'), SETTLE_MS);
  };

  const nearer = (day: Date, s: Date, e: Date): End => {
    if (day < startOfDay(s)) return 'start';
    if (day > startOfDay(e)) return 'end';
    return day.getTime() - startOfDay(s).getTime() < startOfDay(e).getTime() - day.getTime()
      ? 'start'
      : 'end';
  };

  const pick = (day: Date) => {
    clearTimeout(timer.current);
    if (!start) {
      commit(at(day, null), null);
      return;
    }
    if (!end) {
      commit(start, tidy(start, at(day, new Date(start.getTime() + HOUR))));
      settle();
      return;
    }
    const onEnd = isSameDay(day, start) || isSameDay(day, end);
    if (!grab && onEnd) {
      setGrab(isSameDay(day, end) ? 'end' : 'start');
      return;
    }
    const target = grab ?? nearer(day, start, end);
    setGrab(null);
    const s = target === 'start' ? at(day, start) : start;
    const e = target === 'end' ? at(day, end) : end;
    commit(s, tidy(s, e));
  };

  /** The enabled day under a point, from the `data-day` each day button carries. */
  const dayAt = (x: number, y: number) => {
    const el = document.elementFromPoint(x, y)?.closest<HTMLButtonElement>('[data-day]');
    return el && !el.disabled ? Number(el.dataset.day) : null;
  };

  /* Press an end and slide: it follows the finger day by day. On a one-day
     range the first day slid onto says which end is held. */
  const startDrag = (e: React.PointerEvent, day: Date) => {
    if (!start || !end || e.button !== 0) return;
    if (!isSameDay(day, start) && !isSameDay(day, end)) return;
    const s0 = start;
    const e0 = end;
    let held: End | null = isSameDay(s0, e0) ? null : isSameDay(day, s0) ? 'start' : 'end';
    let last = day.getTime();
    const state = { moved: false };
    drag.current = state;
    const move = (ev: PointerEvent) => {
      const t = dayAt(ev.clientX, ev.clientY);
      if (t === null || t === last) return;
      last = t;
      const d = new Date(t);
      held ??= d < startOfDay(s0) ? 'start' : 'end';
      if (held === 'end' ? d < startOfDay(s0) : d > startOfDay(e0)) return;
      state.moved = true;
      clearTimeout(timer.current);
      setGrab(held);
      const s = held === 'start' ? at(d, s0) : s0;
      commit(s, tidy(s, held === 'end' ? at(d, e0) : e0));
    };
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', up);
      if (state.moved) setGrab(null);
      /* Cleared after the click this pointerup is about to fire. */
      window.setTimeout(() => {
        drag.current = null;
      }, 0);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
    document.addEventListener('pointercancel', up);
  };

  const isDisabled = (day: Date) =>
    Boolean(
      (minDate && day < startOfDay(minDate)) ||
      (maxDate && day > startOfDay(maxDate)) ||
      (start && (!end || grab === 'end') && day < startOfDay(start)) ||
      (end && grab === 'start' && day > startOfDay(end)),
    );

  /* Moving the start on one day keeps the length, as a calendar app does. */
  const setStartTime = (t: Date) => {
    if (!start) return;
    let e = end;
    if (end && isSameDay(start, end)) {
      const gap = Math.max(end.getTime() - start.getTime(), 30 * MINUTE);
      e = new Date(Math.min(t.getTime() + gap, lastOn(t).getTime()));
    }
    setClamped(false);
    commit(t, e);
  };

  const setEndTime = (t: Date) => {
    if (!start) return;
    const early = isSameDay(start, t) && t < start;
    setClamped(early);
    commit(start, early ? tidy(start, t) : t);
  };

  const goTo = (to: Step) => {
    clearTimeout(timer.current);
    /* One day and no end yet: the end step makes it a one-day range. */
    if (to === 'end' && start && !end) {
      commit(start, new Date(Math.min(start.getTime() + HOUR, lastOn(start).getTime())));
    }
    setStep(to);
  };

  const next = () => {
    if (step === 'dates' && timed && start && !timesSet) goTo('start');
    else if (step === 'start') goTo('end');
    else fold();
  };

  const clear = () => {
    clearTimeout(timer.current);
    setGrab(null);
    setClamped(false);
    setTimesSet(false);
    setStep('dates');
    commit(null, null);
  };

  const toggle = () => {
    if (open) return fold();
    setView(startOfDay(start ?? (minDate && minDate > new Date() ? minDate : new Date())));
    setStep('dates');
    setOpen(true);
  };

  const shiftView = (by: number) =>
    setView((current) => new Date(current.getFullYear(), current.getMonth() + by, 1));

  const ends = (s: Date, e: Date | null) => {
    const from = timed ? `${dayText(s)}, ${timeText(s)}` : dayText(s);
    if (!e) return from;
    if (isSameDay(s, e)) return timed ? `${from} – ${timeText(e)}` : from;
    return `${from} – ${timed ? `${dayText(e)}, ${timeText(e)}` : dayText(e)}`;
  };

  const endClamped = step === 'end' && clamped;

  const hint = () => {
    if (step === 'start') return '';
    if (endClamped) return 'The end can’t be before the start.';
    if (step === 'end') return start && end ? `Lasts ${lengthText(start, end)}.` : '';
    if (grab) return `Pick a day for the ${grab}.`;
    if (!start) return 'Tap the first day.';
    if (!end) return 'Tap the last day, or the same day again for one day.';
    return 'Drag an end to move it, or tap a day to move the nearer end.';
  };

  const band = Boolean(start && end && !isSameDay(start, end));
  const today = new Date();
  const steps =
    timed && start
      ? [
          { value: 'dates' as const, label: 'Dates' },
          { value: 'start' as const, label: `${startLabel} ${timeText(start)}` },
          { value: 'end' as const, label: end ? `${endLabel} ${timeText(end)}` : endLabel },
        ]
      : null;
  const isNext = step === 'start' || Boolean(step === 'dates' && steps && !timesSet);

  return (
    <div className={clsx(styles.field, className)}>
      {label && (
        <span className={styles.fieldLabel}>
          {label}
          {mandatory && <span>*</span>}
        </span>
      )}
      <button
        type="button"
        className={clsx(styles.trigger, error && styles.triggerError)}
        aria-expanded={open}
        onClick={toggle}
      >
        <span className={clsx(!start && styles.triggerPlaceholder)}>
          {start ? ends(start, end) : placeholder}
          {start && !end && (open || endRequired) && (
            <span className={styles.pickEnd}> – pick end</span>
          )}
        </span>
        <BiCalendarAlt size={20} aria-hidden />
      </button>

      {open && (
        <div className={styles.rangeCard}>
          {onAllDayChange && (
            <Toggle
              className={styles.allDay}
              checked={allDay}
              onChange={(e) => {
                /* The time steps go away under an all-day range; land back on the month. */
                clearTimeout(timer.current);
                setStep('dates');
                onAllDayChange(e.target.checked);
              }}
            >
              All day
            </Toggle>
          )}

          {steps && (
            <SegmentedControl
              size="sm"
              fullWidth
              aria-label="Step"
              options={steps}
              value={step}
              onChange={goTo}
            />
          )}

          {step === 'dates' && (
            <>
              <div className={styles.header}>
                <span className={styles.headerLabel}>
                  {view.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </span>
                <div className={styles.nav}>
                  <button type="button" onClick={() => shiftView(-1)} aria-label="Previous month">
                    <IoChevronBack />
                  </button>
                  <button type="button" onClick={() => shiftView(1)} aria-label="Next month">
                    <IoChevronForward />
                  </button>
                </div>
              </div>
              <div className={styles.weekdays}>
                {WEEKDAYS.map((weekday) => (
                  <span key={weekday}>{weekday}</span>
                ))}
              </div>
              <div className={styles.days}>
                {buildDayCells(view).map((day, index) => {
                  if (!day) return <span key={`empty-${index}`} />;
                  const isStart = isSameDay(day, start);
                  const isEnd = isSameDay(day, end);
                  const inside =
                    band && start && end && day > startOfDay(start) && day < startOfDay(end);
                  return (
                    <div
                      key={day.toISOString()}
                      className={clsx(
                        styles.cell,
                        inside && styles.inRange,
                        band && isStart && styles.rangeStart,
                        band && isEnd && styles.rangeEnd,
                      )}
                    >
                      <button
                        type="button"
                        className={clsx(
                          styles.day,
                          (isStart || isEnd) && styles.daySelected,
                          isSameDay(day, today) && styles.dayToday,
                          grab && (grab === 'start' ? isStart : isEnd) && styles.dayGrabbed,
                          end && (isStart || isEnd) && styles.dayDraggable,
                        )}
                        data-day={day.getTime()}
                        aria-pressed={isStart || isEnd}
                        aria-label={day.toLocaleDateString('en-US', { dateStyle: 'full' })}
                        disabled={isDisabled(day)}
                        onPointerDown={(e) => startDrag(e, day)}
                        onClick={() => !drag.current?.moved && pick(day)}
                      >
                        {day.getDate()}
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {step === 'start' && start && (
            <>
              <p className={styles.stepTitle}>
                {startLabel} on {dayText(start)}
              </p>
              <TimeSelect
                variant="wheel"
                label={startLabel}
                value={start}
                onChange={setStartTime}
              />
            </>
          )}

          {step === 'end' && start && end && (
            <>
              <p className={styles.stepTitle}>
                {endLabel} on {dayText(end)}
              </p>
              <TimeSelect variant="wheel" label={endLabel} value={end} onChange={setEndTime} />
            </>
          )}

          <div className={styles.rangeFoot}>
            <p className={clsx(styles.hint, endClamped && styles.hintError)}>{hint()}</p>
            {!mandatory && start && <ButtonGhost onClick={clear}>Clear Dates</ButtonGhost>}
            {(isNext || !inline) && (
              <ButtonSmall onClick={next}>{isNext ? 'Next' : 'Done'}</ButtonSmall>
            )}
          </div>
        </div>
      )}

      {error && <p className={inputStyles.Error}>{error}</p>}
      {!error && warning && (
        <p className={inputStyles.Warning}>
          <AiOutlineWarning size={14} aria-hidden />
          {warning}
        </p>
      )}
    </div>
  );
};
